import { useEffect, useRef, useState } from 'react'
import { useStore, makeBackup, restoreBackup, type Backup } from '../lib/store.ts'
import { TopBar } from '../components/ui.tsx'
import { daysBetween, fromDayKey } from '../lib/time.ts'

export function SettingsScreen() {
  const { settings, update, cards, daily, now, reset, idx, refresh } = useStore()
  const fileRef = useRef<HTMLInputElement>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [audioOk, setAudioOk] = useState<boolean | null>(null)
  const [caching, setCaching] = useState<{ done: number; total: number } | null>(null)

  // Перевіряємо, чи справді лежить озвучка — щоб не гадати, коли її не чути.
  useEffect(() => {
    const sample = idx.order.map((id) => idx.phraseById.get(id)?.audio).find(Boolean)
    if (!sample) return setAudioOk(false)
    fetch(`${import.meta.env.BASE_URL}audio/${sample}.mp3`, { method: 'HEAD' })
      .then((r) => setAudioOk(r.ok))
      .catch(() => setAudioOk(false))
  }, [idx])

  const exportBackup = () => {
    const data = makeBackup({ cards, settings, daily }, Date.now())
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `polski-progres-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 2000)
    setMessage('Файл збережено. Поклади його в хмару — це єдина копія прогресу.')
  }

  const importBackup = async (file: File) => {
    try {
      const data = JSON.parse(await file.text()) as Backup
      await restoreBackup(data)
      setMessage('Прогрес відновлено. Перезавантажую…')
      setTimeout(() => location.reload(), 900)
    } catch (e) {
      setMessage(`Не вдалося прочитати файл: ${(e as Error).message}`)
    }
  }

  /**
   * Скачує всю озвучку в кеш браузера. Service worker кешує аудіо на вимогу,
   * тому просте прочитання кожного файлу робить курс повністю офлайновим.
   */
  const cacheAllAudio = async () => {
    const keys = [...new Set(idx.order.map((id) => idx.phraseById.get(id)?.audio).filter(Boolean))] as string[]
    setCaching({ done: 0, total: keys.length })
    let done = 0
    const queue = [...keys]
    const worker = async () => {
      while (queue.length) {
        const k = queue.shift()!
        try { await fetch(`${import.meta.env.BASE_URL}audio/${k}.mp3`, { cache: 'force-cache' }) } catch { /* пропускаємо */ }
        done++
        if (done % 5 === 0 || !queue.length) setCaching({ done, total: keys.length })
      }
    }
    await Promise.all([worker(), worker(), worker(), worker()])
    setCaching(null)
    setMessage('Готово: уся озвучка тепер доступна без інтернету.')
  }

  const askNotifications = async () => {
    if (!('Notification' in window)) return setMessage('Цей браузер не вміє показувати сповіщення.')
    const res = await Notification.requestPermission()
    setMessage(res === 'granted' ? 'Дозвіл отримано.' : 'Дозвіл не надано.')
  }

  const daysLeft = settings.deadline ? daysBetween(now, fromDayKey(settings.deadline)) : 0

  return (
    <>
      <TopBar title="Налаштування" />
      <div className="screen stack">
        <section className="card stack--tight stack">
          <strong>Дедлайн</strong>
          <input
            className="input"
            type="date"
            value={settings.deadline}
            onChange={(e) => { update({ deadline: e.target.value }); refresh() }}
          />
          <p className="tiny faint">
            Лишилось {daysLeft} днів. Від цієї дати рахується денна норма й стиснення
            інтервалів повторення.
          </p>
        </section>

        <section className="card stack--tight stack">
          <strong>Транскрипція</strong>
          <div className="stack--tight stack">
            {([
              ['auto', 'Автоматично', 'Перші два тижні показувати, далі приховати'],
              ['always', 'Завжди', 'Зручно, але око не звикає до польського написання'],
              ['never', 'Ніколи', 'Найшвидший шлях до вільного читання'],
            ] as const).map(([value, label, hint]) => (
              <button
                key={value}
                className={`option${settings.transcription === value ? ' is-correct' : ''}`}
                onClick={() => update({ transcription: value })}
              >
                <div style={{ fontWeight: 550 }}>{label}</div>
                <div className="option__uk">{hint}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="card stack--tight stack">
          <strong>Нагадування</strong>
          <input
            className="input"
            type="time"
            value={settings.reminder ?? ''}
            onChange={(e) => update({ reminder: e.target.value || null })}
          />
          <p className="tiny faint">
            Чесно про обмеження: iOS не дає веб-застосункам показувати сповіщення за розкладом,
            коли вони закриті. Тому надійніше зробити нагадування будильником або
            автоматизацією в «Командах». Кнопка нижче вмикає сповіщення, коли застосунок відкритий.
          </p>
          <button className="btn btn--wide" onClick={askNotifications}>Дозволити сповіщення</button>
        </section>

        <section className="card stack--tight stack">
          <strong>Обсяг заняття</strong>
          <label className="small muted">Стеля повторень за одну сесію: {settings.maxReviews}</label>
          <input
            type="range" min={40} max={250} step={10}
            value={settings.maxReviews}
            onChange={(e) => { update({ maxReviews: Number(e.target.value) }); refresh() }}
          />
          <p className="tiny faint">
            Якщо заняття стають задовгими — зменш. Планувальник сам перерозподілить решту
            на наступні дні.
          </p>
        </section>

        <section className="card stack--tight stack">
          <strong>Резервна копія</strong>
          <p className="tiny faint">
            Прогрес зберігається тільки на цьому пристрої. Якщо Safari почистить дані сайту —
            він зникне. Раз на тиждень вивантажуй файл.
          </p>
          <button className="btn btn--wide" onClick={exportBackup}>Вивантажити прогрес у файл</button>
          <button className="btn btn--wide" onClick={() => fileRef.current?.click()}>Відновити з файлу</button>
          <input
            ref={fileRef} type="file" accept="application/json" hidden
            onChange={(e) => e.target.files?.[0] && importBackup(e.target.files[0])}
          />
        </section>

        <section className="card stack--tight stack">
          <strong>Озвучка</strong>
          <p className="small muted">
            {audioOk === null ? 'Перевіряю…'
              : audioOk ? '✅ Аудіофайли на місці.'
              : '⚠️ Аудіофайлів не знайдено. Озвучка піде голосом браузера.'}
          </p>
          {audioOk && (
            <>
              <p className="tiny faint">
                Озвучка кешується в міру прослуховування. Щоб курс працював у літаку й
                у польському метро — скачай усе одразу (близько 14 МБ).
              </p>
              <button className="btn btn--wide" disabled={!!caching} onClick={cacheAllAudio}>
                {caching ? `Завантажую… ${caching.done} / ${caching.total}` : 'Завантажити всю озвучку'}
              </button>
            </>
          )}
        </section>

        <section className="card stack--tight stack">
          <strong>Anki</strong>
          <p className="tiny faint">
            Уся колода з аудіо, 673 нотатки й 1346 карток (впізнавання та відтворення).
            Імпортується в Anki як звичайний файл.
          </p>
          <a className="btn btn--wide" href={`${import.meta.env.BASE_URL}export/polski-anki.apkg`} download>
            Завантажити колоду .apkg (7 МБ)
          </a>
        </section>

        <section className="card stack--tight stack">
          <strong>Скидання</strong>
          {confirmReset ? (
            <>
              <p className="small" style={{ color: 'var(--bad)' }}>
                Це зітре весь прогрес назавжди. Спершу зроби резервну копію.
              </p>
              <button className="btn btn--danger btn--wide" onClick={async () => { await reset(); setConfirmReset(false); setMessage('Прогрес очищено.') }}>
                Так, стерти все
              </button>
              <button className="btn btn--ghost btn--wide" onClick={() => setConfirmReset(false)}>Скасувати</button>
            </>
          ) : (
            <button className="btn btn--danger btn--wide" onClick={() => setConfirmReset(true)}>Стерти прогрес</button>
          )}
        </section>

        {message && <div className="feedback feedback--hint">{message}</div>}

        <p className="tiny faint center">
          Курс: {idx.order.length} фраз у {idx.modules.length} модулях
        </p>
      </div>
    </>
  )
}
